import type { ReactNode } from 'react';
import { cx } from '../cx';
import { type ChartLayout } from './layout';
import { LABEL_TEXT, VALUE_TEXT } from './scale';

export type DivergingBarsProps = {
  /** Signed values against the reference (change, difference); sorted most above to most below. */
  rows: Array<{
    id: string;
    label: ReactNode;
    value: number;
    /** Signed and formatted ("+18%", "−15%"). */ display: ReactNode;
  }>;
  /** The largest distance either side; defaults to the largest absolute value. */
  extent?: number;
  /** `auto` (default) switches to the narrow form in a narrow container: each label above its row, the bars full width. */
  layout?: ChartLayout;
  /** One sentence that states the finding and the key values, for screen readers. */
  description: string;
  className?: string;
};

const ROW: Record<ChartLayout, string> = {
  auto: 'flex flex-col gap-0.5 @md:flex-row @md:items-center @md:gap-3',
  narrow: 'flex flex-col gap-0.5',
  wide: 'flex items-center gap-3',
};
const ROW_LABEL: Record<ChartLayout, string> = {
  auto: '@md:w-1/4 @md:shrink-0',
  narrow: '',
  wide: 'w-1/4 shrink-0',
};
// In a column, flex-1 would collapse the bars to no height: the narrow form sizes them by width.
const BARS: Record<ChartLayout, string> = { auto: '@md:flex-1', narrow: '', wide: 'flex-1' };

/**
 * Bars going right (gold, above or more) or left (iris, below or fewer) of a reference line. Green
 * and red are never the pair, and coral never means above.
 */
export function DivergingBars({ rows, extent, layout = 'auto', description, className }: DivergingBarsProps) {
  const max = extent ?? Math.max(1, ...rows.map((r) => Math.abs(r.value)));
  return (
    <div role="img" aria-label={description} className={cx('@container flex flex-col gap-2', className)}>
      {rows.map((r) => {
        const w = (Math.min(Math.abs(r.value), max) / max) * 100;
        const up = r.value >= 0;
        return (
          <div key={r.id} className={ROW[layout]}>
            <span className={cx('truncate', LABEL_TEXT, ROW_LABEL[layout])}>{r.label}</span>
            <span className={cx('grid min-w-0 grid-cols-2', BARS[layout])}>
              <span className="flex items-center justify-end gap-2 border-r-2 border-jc-ink-secondary pr-0">
                {!up ? (
                  <>
                    <span className={VALUE_TEXT}>{r.display}</span>
                    <span
                      className="block h-3.5 origin-right rounded-l-jc-info bg-jc-chart-div-neg-2 motion-safe:animate-jc-grow-x"
                      style={{ width: `calc((100% - 3.5rem) * ${w / 100})` }}
                    />
                  </>
                ) : null}
              </span>
              <span className="flex items-center gap-2">
                {up ? (
                  <>
                    <span
                      className="block h-3.5 origin-left rounded-r-jc-info bg-jc-chart-div-pos-2 motion-safe:animate-jc-grow-x"
                      style={{ width: `calc((100% - 3.5rem) * ${w / 100})` }}
                    />
                    <span className={VALUE_TEXT}>{r.display}</span>
                  </>
                ) : null}
              </span>
            </span>
          </div>
        );
      })}
    </div>
  );
}
